import { useState, type KeyboardEvent } from 'react'
import { useParams } from 'react-router'
import { Link } from 'react-router'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  FileDown,
  FileUp,
  Keyboard,
  RotateCcw,
  Save,
  Send,
  ShieldCheck,
  Unlock,
} from 'lucide-react'
import {
  MARK_STATUSES,
  checkGrade,
  parseCell,
  parseScore,
  type GradeInput,
  type MarkStatus,
  type SheetInfo,
  type SheetRowInfo,
} from '@concours/shared'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ConfirmModal,
  InfoGrid,
  InfoItem,
  PageHeader,
  Tabs,
  Textarea,
  Modal,
  useAuth,
  useCan,
  useToast,
} from '@concours/ui'
import { errorMessage } from '../../lib/errors'
import { when } from '../../lib/competition-types'
import { GRADING_KEY, SHEET_VARIANT, fmtScore, useSheet, useSheetHistory } from '../../lib/grading'
import { Progress } from './Progress'
import { saveFile, type OfficialDocument } from '../../lib/documents'
import { ImportModal } from './ImportModal'

/** Saisie en cours d'une ligne : statut + texte tapé (note / performance) ou statut choisi. */
export interface Draft {
  status: MarkStatus
  text: string
  code: string
}

type Kind = 'numeric' | 'performance' | 'status'
const kindOf = (sheet: SheetInfo): Kind =>
  !sheet.discipline || sheet.discipline.grading === 'status'
    ? 'status'
    : sheet.discipline.grading === 'performance'
      ? 'performance'
      : 'numeric'

const fromServer = (row: SheetRowInfo, kind: Kind): Draft => ({
  status: row.grade?.status ?? 'present',
  text: fmtScore(kind === 'performance' ? row.grade?.performance : row.grade?.value),
  code: row.grade?.code ?? '',
})

export function toInput(d: Draft, kind: Kind): GradeInput | null {
  if (d.status !== 'present') return { status: d.status }
  if (kind === 'status') return d.code ? { status: 'present', code: d.code } : null
  const n = parseScore(d.text)
  if (n === null) return null
  return kind === 'performance'
    ? { status: 'present', performance: n }
    : { status: 'present', value: n }
}

/**
 * Relevé de notes (cahier §9.5) : grille de saisie au clavier pour l'évaluateur
 * (Entrée / flèches pour changer de ligne, « ABS » / « DISP »), lecture et
 * validation pour la commission. Chaque modification est historisée.
 */
export function SheetPage() {
  const { sheetId = '' } = useParams()
  const query = useSheet(sheetId)
  if (query.isError) return <Alert tone="danger">{errorMessage(query.error)}</Alert>
  if (!query.data)
    return (
      <Card>
        <CardBody>Chargement…</CardBody>
      </Card>
    )
  return <SheetView key={query.data.id + query.data.status} sheet={query.data} />
}

function SheetView({ sheet }: { sheet: SheetInfo }) {
  const { session } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const kind = kindOf(sheet)
  const [tab, setTab] = useState<'grid' | 'history'>('grid')
  const [edits, setEdits] = useState<Record<string, Draft>>({})
  const [modal, setModal] = useState<
    null | 'save-reason' | 'submit' | 'reopen' | 'validate' | 'unlock' | 'import'
  >(null)
  const history = useSheetHistory(sheet.id, tab === 'history')
  const canPv = useCan('pv.generate', { type: 'competition', id: sheet.stage.competitionId })
  const official = useMutation({
    mutationFn: async () => {
      const doc = await session.api.post<OfficialDocument>(`/admin/sheets/${sheet.id}/document`)
      saveFile(
        await session.api.download(`/admin/official-documents/${doc.id}/pdf`),
        `releve-${doc.code}.pdf`
      )
    },
    onError: (error) => toast.error(errorMessage(error)),
  })
  const editable = sheet.can.enter
  const statuses = sheet.discipline?.statuses ?? sheet.stage.statuses

  const current = (row: SheetRowInfo) => edits[row.stageCandidateId] ?? fromServer(row, kind)
  const errorOf = (row: SheetRowInfo) => {
    const input = toInput(current(row), kind)
    if (!input) {
      const d = current(row)
      return d.status === 'present' &&
        kind !== 'status' &&
        d.text.trim() &&
        Number.isNaN(parseScore(d.text))
        ? 'Saisie non reconnue.'
        : null
    }
    return checkGrade(input, sheet.discipline, sheet.stage.statuses, row.gender).error
  }
  const dirtyIds = Object.keys(edits).filter((id) => {
    const row = sheet.rows.find((r) => r.stageCandidateId === id)!
    const a = edits[id]!
    const b = fromServer(row, kind)
    return a.status !== b.status || a.text.trim() !== b.text || a.code !== b.code
  })
  const errors = sheet.rows.filter((r) => errorOf(r))
  const complete = sheet.rows.filter((r) => {
    const d = current(r)
    return d.status !== 'present' || toInput(d, kind) !== null
  }).length

  const invalidate = () => queryClient.invalidateQueries({ queryKey: GRADING_KEY })

  const save = useMutation({
    mutationFn: (reason: string | null) =>
      session.api.put<{ changed: number }>(`/admin/sheets/${sheet.id}/grades`, {
        rows: dirtyIds.map((id) => {
          const row = sheet.rows.find((r) => r.stageCandidateId === id)!
          const input = toInput(edits[id]!, kind) ?? { status: 'present' as const }
          return { stageCandidateId: row.stageCandidateId, ...input }
        }),
        reason,
      }),
    onSuccess: async (result) => {
      toast.success(`${result.changed} note(s) enregistrée(s)`)
      setEdits({})
      setModal(null)
      await invalidate()
    },
    onError: (error) => toast.error(errorMessage(error)),
  })
  const action = useMutation({
    mutationFn: ({ kind: k, reason }: { kind: string; reason?: string }) =>
      session.api
        .post<unknown>(`/admin/sheets/${sheet.id}/${k}`, reason ? { reason } : {})
        .then(() => undefined),
    onSuccess: async (_, { kind: k }) => {
      toast.success(
        {
          submit: 'Relevé soumis à la commission',
          reopen: 'Relevé rouvert pour correction',
          validate: 'Relevé validé',
          unlock: 'Relevé déverrouillé',
        }[k] ?? 'Enregistré'
      )
      setModal(null)
      await invalidate()
    },
    onError: (error) => toast.error(errorMessage(error)),
  })
  const doSave = () => {
    if (!dirtyIds.length || errors.length) return
    if (sheet.status === 'unlocked') setModal('save-reason')
    else save.mutate(null)
  }

  const set = (id: string, patch: Partial<Draft>) =>
    setEdits((e) => {
      const row = sheet.rows.find((r) => r.stageCandidateId === id)!
      return { ...e, [id]: { ...(e[id] ?? fromServer(row, kind)), ...patch } }
    })
  const focusRow = (index: number) => {
    const el = document.querySelector<HTMLElement>(`[data-grade-row="${index}"]`)
    el?.focus()
    if (el instanceof HTMLInputElement) el.select()
  }
  const onKey = (event: KeyboardEvent<HTMLElement>, index: number, row: SheetRowInfo) => {
    if ((event.ctrlKey || event.metaKey) && event.key === 's') {
      event.preventDefault()
      doSave()
      return
    }
    if (
      event.key === 'Enter' ||
      (event.key === 'ArrowDown' && !(event.target instanceof HTMLSelectElement))
    ) {
      event.preventDefault()
      if (event.target instanceof HTMLInputElement) {
        const parsed = parseCell(event.target.value)
        if (parsed.status !== 'present')
          set(row.stageCandidateId, { status: parsed.status, text: '' })
      }
      focusRow(index + 1)
    } else if (event.key === 'ArrowUp' && !(event.target instanceof HTMLSelectElement)) {
      event.preventDefault()
      focusRow(index - 1)
    }
  }

  return (
    <>
      <PageHeader
        eyebrow={
          <Link
            to={
              sheet.can.enter || sheet.can.submit || sheet.can.reopen
                ? '/notes'
                : `/commissions/${sheet.stage.id}`
            }
            className="link"
          >
            {sheet.stage.competitionTitle}
          </Link>
        }
        title={`${sheet.discipline?.label ?? 'Statut'} — ${sheet.lot.label}`}
        description={`${sheet.stage.label} · correction ${sheet.round}${sheet.commission ? ` · ${sheet.commission}` : ''}`}
        actions={<Badge variant={SHEET_VARIANT[sheet.status]}>{sheet.statusLabel}</Badge>}
      />

      <div className="sheet-layout">
        <Card>
          <CardBody>
            <InfoGrid>
              <InfoItem label="Évaluateur">{sheet.evaluator.name}</InfoItem>
              <InfoItem label="Salle / centre">
                {[sheet.lot.room, sheet.lot.center].filter(Boolean).join(' · ') || '—'}
              </InfoItem>
              <InfoItem label="Barème">
                {sheet.discipline && sheet.discipline.grading !== 'status'
                  ? `${fmtScore(sheet.discipline.minScore)} à ${fmtScore(sheet.discipline.maxScore)} · pas ${fmtScore(sheet.discipline.precision)}`
                  : 'Statut'}
              </InfoItem>
              <InfoItem label="Avancement">
                <Progress done={complete} total={sheet.rows.length} label="Avancement du relevé" />
              </InfoItem>
              <InfoItem label="Soumis le">{when(sheet.submittedAt)}</InfoItem>
              <InfoItem label="Validé">
                {sheet.validatedBy ? `${sheet.validatedBy.name}, ${when(sheet.validatedAt)}` : '—'}
              </InfoItem>
            </InfoGrid>
          </CardBody>
        </Card>

        {sheet.anonymous && (
          <Alert tone="info">
            Correction anonyme : les candidats sont désignés par un code. L’anonymat est levé par la
            commission une fois tous les relevés validés.
          </Alert>
        )}
        {sheet.status === 'unlocked' && (
          <Alert tone="warning">
            Relevé rouvert après validation : chaque correction exige un motif, conservé dans
            l’historique.
          </Alert>
        )}
        {sheet.status === 'submitted' && !sheet.can.validate && (
          <Alert tone="info">Relevé soumis : en attente de validation par la commission.</Alert>
        )}
        {sheet.status === 'validated' && (
          <Alert tone="success">
            <span className="row" style={{ gap: 'var(--space-3)', flexWrap: 'wrap' }}>
              <span>Relevé validé : les notes sont verrouillées.</span>
              {canPv && (
                <Button
                  size="sm"
                  variant="secondary"
                  icon={<FileDown />}
                  loading={official.isPending}
                  onClick={() => official.mutate()}
                >
                  Relevé officiel (PDF)
                </Button>
              )}
            </span>
          </Alert>
        )}

        <div className="row sheet-actions">
          {editable && (
            <>
              <Button icon={<FileUp />} onClick={() => setModal('import')}>
                Importer (CSV)
              </Button>
              <Button
                icon={<Save />}
                loading={save.isPending}
                disabled={!dirtyIds.length || errors.length > 0}
                onClick={doSave}
              >
                Enregistrer{dirtyIds.length ? ` (${dirtyIds.length})` : ''}
              </Button>
            </>
          )}
          {sheet.can.submit && (
            <Button
              variant="primary"
              icon={<Send />}
              disabled={dirtyIds.length > 0 || complete < sheet.rows.length}
              onClick={() => setModal('submit')}
            >
              Soumettre le relevé
            </Button>
          )}
          {sheet.can.reopen && (
            <Button icon={<RotateCcw />} onClick={() => setModal('reopen')}>
              Rouvrir pour corriger
            </Button>
          )}
          {sheet.can.validate && (
            <Button variant="primary" icon={<ShieldCheck />} onClick={() => setModal('validate')}>
              Valider le relevé
            </Button>
          )}
          {sheet.can.unlock && (
            <Button icon={<Unlock />} onClick={() => setModal('unlock')}>
              Déverrouiller
            </Button>
          )}
        </div>
        {sheet.can.submit && (dirtyIds.length > 0 || complete < sheet.rows.length) && (
          <p className="muted" style={{ margin: 0 }}>
            {dirtyIds.length
              ? 'Enregistrez vos saisies avant de soumettre.'
              : `Soumission possible quand chaque candidat a une note ou un statut (${sheet.rows.length - complete} restant(s)).`}
          </p>
        )}

        <Tabs
          label="Relevé"
          value={tab}
          onChange={(v) => setTab(v as 'grid' | 'history')}
          items={[
            { value: 'grid', label: `Notes · ${sheet.rows.length}` },
            { value: 'history', label: 'Historique des modifications' },
          ]}
        />

        {tab === 'grid' && (
          <Card>
            {editable && (
              <p className="muted sheet-hint">
                <Keyboard aria-hidden="true" size={14} /> Entrée ou ↓ : ligne suivante · ↑ :
                précédente · « ABS » absent · « DISP » dispensé · Ctrl+S : enregistrer
              </p>
            )}
            <div className="tbl-wrap">
              <table className="tbl grade-grid">
                <caption className="sr-only">Notes du lot {sheet.lot.label}</caption>
                <thead>
                  <tr>
                    <th scope="col">{sheet.anonymous ? 'Code' : 'Candidat'}</th>
                    <th scope="col">Présence</th>
                    <th scope="col">
                      {kind === 'performance'
                        ? `Performance (${sheet.discipline?.scale?.unit === 'seconds' ? 's' : sheet.discipline?.scale?.unit === 'meters' ? 'm' : 'pts'})`
                        : kind === 'status'
                          ? 'Statut'
                          : 'Note'}
                    </th>
                    {kind === 'performance' && <th scope="col">Note</th>}
                  </tr>
                </thead>
                <tbody>
                  {sheet.rows.map((row, index) => {
                    const d = current(row)
                    const error = errorOf(row)
                    const input = toInput(d, kind)
                    const computed =
                      kind === 'performance' && input
                        ? checkGrade(input, sheet.discipline, [], row.gender).value
                        : null
                    const label = row.name ? `${row.name} (${row.label})` : row.label
                    return (
                      <tr
                        key={row.stageCandidateId}
                        data-dirty={dirtyIds.includes(row.stageCandidateId) || undefined}
                        data-error={error ? true : undefined}
                      >
                        <th scope="row">
                          <span className="stack" style={{ gap: 0 }}>
                            <span className={row.name ? undefined : 'mono'}>
                              {row.name ?? row.label}
                            </span>
                            {row.name && <span className="mono muted">{row.label}</span>}
                          </span>
                        </th>
                        <td>
                          <select
                            className="select select-sm"
                            aria-label={`Présence — ${label}`}
                            disabled={!editable}
                            value={d.status}
                            onChange={(e) =>
                              set(row.stageCandidateId, { status: e.target.value as MarkStatus })
                            }
                          >
                            {Object.entries(MARK_STATUSES).map(([value, l]) => (
                              <option key={value} value={value}>
                                {l}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          {kind === 'status' ? (
                            <select
                              className="select select-sm"
                              data-grade-row={index}
                              aria-label={`Statut — ${label}`}
                              disabled={!editable || d.status !== 'present'}
                              value={d.status === 'present' ? d.code : ''}
                              onKeyDown={(e) => onKey(e, index, row)}
                              onChange={(e) => set(row.stageCandidateId, { code: e.target.value })}
                            >
                              <option value="">—</option>
                              {statuses.map((s) => (
                                <option key={s.code} value={s.code}>
                                  {s.label}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <input
                              className="input input-sm grade-input"
                              data-grade-row={index}
                              inputMode="decimal"
                              autoComplete="off"
                              aria-label={`${kind === 'performance' ? 'Performance' : 'Note'} — ${label}`}
                              aria-invalid={error ? true : undefined}
                              readOnly={!editable}
                              value={
                                d.status === 'present'
                                  ? d.text
                                  : d.status === 'absent'
                                    ? 'ABS'
                                    : 'DISP'
                              }
                              onKeyDown={(e) => onKey(e, index, row)}
                              onBlur={(e) => {
                                const parsed = parseCell(e.target.value)
                                if (d.status === 'present' && parsed.status !== 'present')
                                  set(row.stageCandidateId, { status: parsed.status, text: '' })
                              }}
                              onChange={(e) => {
                                const v = e.target.value
                                if (d.status !== 'present')
                                  set(row.stageCandidateId, {
                                    status: 'present',
                                    text: v.replace(/[^\d.,]/g, ''),
                                  })
                                else set(row.stageCandidateId, { text: v })
                              }}
                            />
                          )}
                          {error && <span className="grade-error">{error}</span>}
                        </td>
                        {kind === 'performance' && (
                          <td className="num">{fmtScore(computed) || '—'}</td>
                        )}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {tab === 'history' && (
          <Card>
            <CardHeader title="Historique des modifications" />
            <div className="tbl-wrap">
              <table className="tbl">
                <caption className="sr-only">Historique des modifications</caption>
                <thead>
                  <tr>
                    <th scope="col">Date</th>
                    <th scope="col">Candidat</th>
                    <th scope="col">Avant</th>
                    <th scope="col">Après</th>
                    <th scope="col">Auteur</th>
                    <th scope="col">Motif</th>
                  </tr>
                </thead>
                <tbody>
                  {(history.data ?? []).map((h) => (
                    <tr key={h.id}>
                      <td>{when(h.at)}</td>
                      <td className="mono">{h.candidate}</td>
                      <td>{h.before}</td>
                      <td>
                        <strong>{h.after}</strong>
                      </td>
                      <td>{h.author}</td>
                      <td>{h.reason ?? '—'}</td>
                    </tr>
                  ))}
                  {history.data?.length === 0 && (
                    <tr>
                      <td colSpan={6} className="muted">
                        Aucune modification.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>

      {modal === 'save-reason' && (
        <ReasonModal
          title="Motif de la correction"
          message="Le relevé a été rouvert après validation : le motif est conservé dans l’historique de chaque note modifiée."
          confirm="Enregistrer"
          loading={save.isPending}
          onClose={() => setModal(null)}
          onConfirm={(reason) => save.mutate(reason)}
        />
      )}
      {modal === 'import' && (
        <ImportModal
          sheet={sheet}
          kind={kind}
          onClose={() => setModal(null)}
          onApply={(drafts) => {
            setEdits((e) => ({ ...e, ...drafts }))
            setModal(null)
            toast.success(
              `${Object.keys(drafts).length} ligne(s) reprise(s) — vérifiez puis enregistrez`
            )
          }}
        />
      )}
      <ConfirmModal
        open={modal === 'submit' && sheet.status === 'draft'}
        onClose={() => setModal(null)}
        title="Soumettre le relevé ?"
        message="Il est transmis à la commission pour validation. Une correction ultérieure exigera de le rouvrir avec un motif."
        confirmLabel="Soumettre"
        onConfirm={() => action.mutateAsync({ kind: 'submit' })}
      />
      <ConfirmModal
        open={modal === 'submit' && sheet.status === 'unlocked'}
        onClose={() => setModal(null)}
        title="Soumettre à nouveau le relevé ?"
        message="Le relevé avait été rouvert après validation : indiquez le motif de la nouvelle soumission."
        confirmLabel="Soumettre"
        reasonRequired
        reasonLabel="Motif"
        reasonMinLength={10}
        onConfirm={(reason) => action.mutateAsync({ kind: 'submit', reason })}
      />
      <ConfirmModal
        open={modal === 'reopen'}
        onClose={() => setModal(null)}
        title="Rouvrir le relevé ?"
        message="Le relevé repasse en saisie, avant validation par la commission. Le motif est journalisé."
        confirmLabel="Rouvrir"
        reasonRequired
        reasonLabel="Motif de la correction"
        reasonMinLength={10}
        onConfirm={(reason) => action.mutateAsync({ kind: 'reopen', reason })}
      />
      <ConfirmModal
        open={modal === 'validate'}
        onClose={() => setModal(null)}
        title="Valider le relevé ?"
        message="Les notes sont verrouillées. Une modification ultérieure exigera un déverrouillage motivé."
        confirmLabel="Valider"
        onConfirm={() => action.mutateAsync({ kind: 'validate' })}
      />
      <ConfirmModal
        open={modal === 'unlock'}
        onClose={() => setModal(null)}
        tone="danger"
        title="Déverrouiller le relevé validé ?"
        message="L’évaluateur pourra corriger ses notes, chacune avec un motif. L’opération est journalisée."
        confirmLabel="Déverrouiller"
        reasonRequired
        reasonLabel="Motif du déverrouillage"
        reasonMinLength={10}
        onConfirm={(reason) => action.mutateAsync({ kind: 'unlock', reason })}
      />
    </>
  )
}

function ReasonModal({
  title,
  message,
  confirm,
  loading,
  onClose,
  onConfirm,
}: {
  title: string
  message: string
  confirm: string
  loading: boolean
  onClose: () => void
  onConfirm: (reason: string) => void
}) {
  const [reason, setReason] = useState('')
  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      description={message}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Annuler
          </Button>
          <Button
            variant="primary"
            loading={loading}
            disabled={reason.trim().length < 10}
            onClick={() => onConfirm(reason.trim())}
          >
            {confirm}
          </Button>
        </>
      }
    >
      <Textarea
        label="Motif"
        required
        rows={3}
        hint="10 caractères minimum."
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
    </Modal>
  )
}
